/** @jsxImportSource @emotion/react */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import Image from 'components/global/Image';
import ProgressBar from 'components/global/ProgressBar';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { FORM } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faArrowUp, faX } from '@fortawesome/pro-solid-svg-icons';

import Button from './Button';
import * as S from './styled/Avatar.styled';

/**
 * @author @buunsal
 * @description Avatar allow users to represent user with icon or image.
 * @param {Object} props - props of the component.
 * @param {'small' | 'medium' | 'large'} [props.size] - The size of the component.
 * @param {string} [props.imageAlt] - Image alt text for the avatar, when the image cannot be uploaded.
 * @param {'square'|'circle'} [props.shape] - the container shape for the component.
 * @param {Object} [props.value] - value prop form the form
 * @param {Object} [props.setValue] - setValue prop form the form
 * @param {*} [props.children] - The content of the component. If it's empty only label and icon will be shown.
 * @returns {object} - Avatar object with the given props.
 * @example
 * import Avatar from 'components/field/Avatar';
 *
 * <Avatar
 * 	name="avatar"
 * 	setValue={(value) => {
							setValue('avatar', value,{ shouldValidate: true, shouldDirty: true });

						}}
 * 	isLoading={isLoading}
 * 	maxFileSize={1000000}
 * 	className="myStyle"
 * 	size="large"
 * />
 */
const Avatar = forwardRef((props, ref) => {
	const {
		size = 'medium',
		value,
		setValue,
		defaultImage,
		maxFileSize,
		imageAlt = 'avatar',
		shape = 'circle',
		children,
		...moreProps
	} = props;

	const innerRef = useRef(null);

	useImperativeHandle(ref, () => innerRef.current);

	const [uploadPercentage, setUploadPercentage] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedFile, setSelectedFile] = useState();
	const [deleteIcon, setDeleteIcon] = useState(false);
	const [uploadBadge, setUploadBadge] = useState(true);
	const { showError } = useAlert();

	const onRemoveFile = () => {
		setValue(null);
		URL.revokeObjectURL(selectedFile?.objectURL);
		setSelectedFile(null);
		setDeleteIcon(false);
	};

	const axiosPrivate = useAxiosPrivate();

	const onFileChange = async ({ target: { files } }) => {
		try {
			setIsLoading(true);

			setUploadPercentage(0);

			setSelectedFile(files[0]);

			if (files[0].size > maxFileSize) {
				showError({ header: 'fileSize' });
				setValue(null);
				URL.revokeObjectURL(selectedFile?.objectURL);
				setSelectedFile(null);
				setDeleteIcon(false);
				setIsLoading(false);
				return;
			}

			const imageFile = files[0];
			imageFile.objectURL = URL.createObjectURL(imageFile);
			setSelectedFile(imageFile);

			const formData = new FormData();

			formData.append('file', files[0]);
			formData.append('name', files[0].name);
			formData.append('type', 'import');

			const config = {
				method: 'post',
				url: FORM.UPLOAD_FILE,
				data: formData,
				headers: {
					'Content-Type': 'multipart/form-data'
				},
				transformRequest: () => formData,
				onUploadProgress: ({ loaded, total }) => {
					setUploadPercentage(parseInt(Math.round((loaded * 100) / total)));
				}
			};

			const response = await axiosPrivate.request(config);

			const { path } = response.data;

			setValue(path);

			// showSuccess({ header: 'fileUploaded' });
		} catch (error) {
			AppLogger.error(error);
			if (error.response === 500) {
				showError({ header: 'serverError' });
			} else {
				showError({});
			}
			setDeleteIcon(false);
		}

		setIsLoading(false);
	};
	useEffect(() => {
		if (value) {
			setDeleteIcon(true);
			setUploadBadge(false);
		} else {
			setDeleteIcon(false);
			setUploadBadge(true);
		}
	}, [value]);

	return (
		<S.AvatarContainer ref={innerRef} className={`avatar ${size} ${shape}`} {...moreProps}>
			{isLoading ? (
				<ProgressBar className="progressBar" value={uploadPercentage}></ProgressBar>
			) : (
				<label>
					{value ? (
						<Image url={value} alt={imageAlt} width="inherit" height="inherit" />
					) : selectedFile ? (
						<Image url={selectedFile.objectURL} alt={imageAlt} width="inherit" height="inherit" />
					) : (
						<Image url={defaultImage} width="inherit" height="inherit" />
					)}

					{uploadBadge && (
						<Button
							id="uploadButton"
							component="span"
							icon={faArrowUp}
							iconButton
							iconSize="x-small"
							variant="borderless"
						/>
					)}

					<input
						hidden
						onChange={(e) => onFileChange(e)}
						type="file"
						id="imageInput"
						accept=".png,.jpeg,.jpg"></input>
				</label>
			)}
			{deleteIcon && (
				<Button
					id="deleteButton"
					component="span"
					icon={faX}
					iconButton
					iconSize="x-small"
					variant="borderless"
					onClick={onRemoveFile}
				/>
			)}
			{children ? children : undefined}
		</S.AvatarContainer>
	);
});

Avatar.displayName = 'Avatar';

export default Avatar;
